import { useEffect, useState } from "react";
import { Check, Cloud, CloudOff, Loader2, RefreshCw } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTabs } from "@/store/tabs";
import { useUi } from "@/store/ui";
import { resolveItem, useAllSpaceItems } from "@/data/store";
import { useAuth } from "@/data/auth";
import { useSyncState } from "@/data/sync";
import { useSignInDialog } from "@/components/SignInDialog";
import { fmtBytes } from "@/components/quota";
import { useSelectionStats } from "@/editor/selection-stats";
import type { NoteHandle } from "@/data/notes";
import s from "./StatusBar.module.css";

/**
 * Bottom status bar. The sync chip is the single most important pixel of
 * trust in the app: it always tells the truth about where your notes are.
 */
export function StatusBar() {
  const spaces = useAllSpaceItems();
  const items = spaces.flatMap((space) => space.items);
  const { activeApp, activeTaskView, cardMode } = useUi();
  const activeItemId = useTabs((s) => {
    const g = s.groups.find((g) => g.id === s.activeGroupId);
    return g?.tabs.find((t) => t.id === g.activeTabId)?.itemId ?? null;
  });

  const crumbs: string[] = [];
  if (activeApp === "notes") {
    let cur = activeItemId;
    let hops = 0;
    while (cur && hops++ < 20) {
      const item = items.find((i) => i.id === cur);
      if (!item) break;
      crumbs.unshift(item.title || "Untitled");
      cur = item.parentId;
    }
  } else if (activeApp === "tasks") {
    const list = activeTaskView === "overview" ? null : items.find((item) => item.id === activeTaskView);
    crumbs.push(list?.title || "Tasks overview");
  } else if (activeApp === "graph") {
    crumbs.push("Graph");
  } else {
    crumbs.push(cardMode === "archive" ? "Cards archive" : cardMode === "trash" ? "Cards trash" : "Cards");
  }

  const activeItem = items.find((i) => i.id === activeItemId);

  return (
    <footer className={s.bar}>
      <SyncChip />
      <span className={s.breadcrumb}>{crumbs.join("  ›  ")}</span>
      <span className={s.meta}>
        {activeApp === "notes" && activeItem?.type === "note" ? <NoteStats noteId={activeItem.id} /> : null}
        <span>v{__APP_VERSION__}</span>
      </span>
    </footer>
  );
}

interface Stats {
  bytes: number;
  chars: number;
  words: number;
  lines: number;
}

/** Nerd stats for the open note; live, debounced, gone when no note is. */
function NoteStats({ noteId }: { noteId: string }) {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let handle: NoteHandle | null = resolveItem(noteId)?.ctx.notes.open(noteId) ?? null;
    if (!handle) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const compute = () => {
      if (!handle) return;
      const text = handle.text.toString();
      setStats({
        bytes: new TextEncoder().encode(text).length,
        chars: text.length,
        words: text.trim() ? text.trim().split(/\s+/).length : 0,
        lines: text ? text.split("\n").length : 0,
      });
    };
    const onUpdate = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(compute, 250);
    };
    void handle.whenLoaded.then(compute);
    handle.doc.on("update", onUpdate);

    return () => {
      if (timer) clearTimeout(timer);
      handle?.doc.off("update", onUpdate);
      handle?.release();
      handle = null;
      setStats(null);
    };
  }, [noteId]);

  const selection = useSelectionStats((s) => s.stats);

  if (!stats) return null;
  const n = (v: number) => v.toLocaleString();
  const readMinutes = Math.max(1, Math.round(stats.words / 225));
  return (
    <span className={s.stats} title="Size, characters, words, lines, reading time">
      {selection ? (
        <span className={s.selStat}>
          {n(selection.words)} word{selection.words === 1 ? "" : "s"} selected
        </span>
      ) : null}
      <span>{fmtBytes(stats.bytes)}</span>
      <span>{n(stats.chars)} chars</span>
      <span>{n(stats.words)} words</span>
      <span>{n(stats.lines)} lines</span>
      <span>~{readMinutes} min</span>
    </span>
  );
}

function SyncChip() {
  const { status: authStatus, user, signOut } = useAuth();
  const sync = useSyncState();
  const openDialog = useSignInDialog((d) => d.setOpen);

  if (authStatus !== "signedIn") {
    return (
      <button
        type="button"
        className={[s.chip, s.chipLocal].join(" ")}
        title="Your work is saved in this browser only. Click to sign in and back it up."
        onClick={() => openDialog(true)}
      >
        <span className={s.dot} />
        Local only
      </button>
    );
  }

  const chip = (() => {
    switch (sync.status) {
      case "offline":
        return (
          <button type="button" className={[s.chip, s.chipOffline].join(" ")} title="Offline — edits are saved on this device and will sync when you're back online.">
            <CloudOff size={11} />
            Offline
          </button>
        );
      case "syncing":
        return (
          <button type="button" className={[s.chip, s.chipSyncing].join(" ")} title={`Syncing ${sync.dirtyDocs} change${sync.dirtyDocs === 1 ? "" : "s"}…`}>
            <RefreshCw size={11} className={s.spin} />
            Syncing…
          </button>
        );
      case "connecting":
        return (
          <button type="button" className={[s.chip, s.chipMuted].join(" ")} title="Connecting to sync…">
            <Loader2 size={11} className={s.spin} />
            Connecting
          </button>
        );
      default:
        return (
          <button type="button" className={[s.chip, s.chipSynced].join(" ")} title="All changes synced to the cloud.">
            <Check size={11} />
            Synced
          </button>
        );
    }
  })();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{chip}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="hn-menu" side="top" align="start" sideOffset={6}>
          <div className="hn-menu-label">{user?.email}</div>
          <DropdownMenu.Item className="hn-menu-item" disabled>
            <Cloud /> Cloud sync is on
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="hn-menu-sep" />
          <DropdownMenu.Item className="hn-menu-item" onSelect={() => void signOut()}>
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
